/**
 * What the product does, at the granularities the surfaces need:
 *
 * - `featureGroups`     — the six pillars, for the website's overview sections.
 * - `capabilityMap`     — capability detail, grouped the way the desktop app groups it.
 * - `storeFeatures`     — the Microsoft Store's "Product features" list.
 * - `platformMatrix`    — what each client does: desktop, web, mobile.
 * - `webApp`            — the web app described on its own, and how it differs
 *                         from the desktop (`clientComparison`).
 *
 * These are different shapes for different audiences, not duplicates: a Store
 * feature is one line under 200 characters, a website pillar is a heading plus
 * a paragraph. Keeping them in one file is what makes a drift visible in review.
 */
import { urls } from './identity.js';
/**
 * The six pillars the product is organised around. These mirror the desktop
 * application's workspaces (Operations, Fleet, Finance, Insights,
 * Administration) plus the offline-first foundation underneath all of them.
 */
export const featureGroups = [
    {
        icon: 'route',
        title: 'Trips and dispatch',
        body: 'Plan trips, assign vehicle and driver, track transit and unloading status, chase PODs, see each trip’s margin, and generate the paperwork each trip needs.',
    },
    {
        icon: 'wrench',
        title: 'Job cards and inventory',
        body: 'Raise workshop job cards against a vehicle, issue parts from FIFO-costed stock, and track tyres as serialized assets through their whole life.',
    },
    {
        icon: 'chart',
        title: 'Money and settlements',
        body: 'One ledger behind trips, bills, receipts, vouchers and party settlements — with rate cards for agreed fares, loan instalment schedules, and an action centre that ranks today’s collection work and sends the reminder by WhatsApp or email.',
    },
    {
        icon: 'pin',
        title: 'Tracking and visibility',
        body: 'Live positions from your tracking provider or the driver’s phone, trips that advance as the truck crosses its route, pinned routes the driver’s phone navigates, and a fleet wall for the office TV with each trip’s planned road and detours drawn.',
    },
    {
        icon: 'fuel',
        title: 'Fuel and toll',
        body: 'Fuel-card balances, card limits and PIN controls, pump credit-bill imports, petro transactions, and NETC toll transactions reconciled against the trips that incurred them.',
    },
    {
        icon: 'offline',
        title: 'Offline-first by design',
        body: 'Every record is written to a local database first and syncs through the Transport Book server when the connection returns. Weak signal at a plant or depot never stops the work — and the same workspace opens in a browser when there is nothing to install.',
    },
];
/**
 * Capability detail, grouped the way the desktop application groups it. The
 * website's features page and solution pages slice this by `area`.
 */
export const capabilityMap = [
    {
        area: 'operations',
        title: 'Operations',
        body: 'The daily work: what is moving, what it cost, and what still needs chasing.',
        items: [
            'Trips with status, route, party, driver, fare, adjustments, and margin',
            'Job cards for workshop and maintenance work, on desktop, web, and mobile',
            'Fuel transactions, pump credit-bill imports, and NETC toll transactions',
            'Reconciliation against bank and fuel-card statements, with matching that learns from your decisions',
            'GPS tracking with map and list views, kept vehicle tracks, and trips that advance from the truck’s position',
            'A fleet wall for the office TV, cycling through the trucks on the move with roads and detours drawn',
            'Pinned trip routes, navigated stop by stop on the driver’s phone',
            'Global search and open-by-id across every record',
            'Inquiry support for questions raised against a trip',
        ],
    },
    {
        area: 'fleet',
        title: 'Fleet and people',
        body: 'The vehicles and the teams behind them, with their papers and money attached.',
        items: [
            'Vehicles with documents, insurance, loan and hypothecation terms, and expiry reminders',
            'Service schedules that remind when work falls due and advance when a service job card closes',
            'Purchase and sale lifecycle, with the linked money on both sides',
            'Driver history and toll history on every truck',
            'Tyres tracked as serialized assets, fitted and rotated by position',
            'Employees, salary processing, and driver availability',
            'Driver Finder for matching an available driver to a load',
            'Parties, firms, and billing profiles',
        ],
    },
    {
        area: 'finance',
        title: 'Finance',
        body: 'One ledger of record, with vouchers and reports as packaging over it.',
        items: [
            'Accounts across cash, bank, fuel card, loan, and statement sources',
            'Party settlements: trips and payments in one basket, closed at parity',
            'Receipts, payments, and credit applied across documents',
            'Billing section: invoices, receipts, payment vouchers, GST filing, e-invoice and e-way bill tools, TDS register',
            'Rate cards that hold the agreed fare per party or vehicle owner and fill it on the trip',
            'Income and expense heads on every record, and capital accounts for partners’ money',
            'Invoices, statements and payment reminders sent by email or WhatsApp',
            'Loan instalment schedules matched to bank debits, with the liability report to follow',
            'Party ledger exports and shareable statement links with password and expiry',
            'Outstanding balances — to-pay, credit, and balanced — in one report',
            'Tally export, balance sheet, and profit and loss',
        ],
    },
    {
        area: 'insights',
        title: 'Insights and controls',
        body: 'Reports that answer an operating question, and checks that keep the data honest.',
        items: [
            'Vehicle, trip expense, and vehicle-financial summaries',
            'Round-trip analysis and loading workspace',
            'Collections and risk scoring across parties',
            'Intelligence: cost per km, lane profitability, leakage checks, fleet utilisation, cash forecast, and compliance registers',
            'Data Quality Center for inconsistent records, and a GPS log review for duplicates, conflicts, and silent units',
            'Document health check with one repair per problem, including recovery from another of your computers',
            'Entity Unifier for merging duplicate master records',
            'Role and module permissions per team, and Connected Devices for every synced machine',
        ],
    },
];
/**
 * Microsoft Store "Product features": one input box per line, at most 20 lines
 * of at most 200 characters. They render as bullets in the order given.
 * `assertStoreListingLimits` enforces both limits before a submission is built.
 */
export const storeFeatures = [
    'Works offline and syncs when connected — a dropped line never stops the office',
    'The same workspace in the browser at my.transportbook.app, with nothing to install',
    'Plan trips and sub-trips from booking through delivery and POD collection, with the margin on each and the fare filled from rate cards',
    'See pending work at a glance — PODs not received, loading slips not raised, trips not closed',
    'One master record set for parties, trucks, drivers, firms and companies, with global search and open-by-id',
    'Bills, receipts and settlements matched to the right trips, with part payments and on-account credit',
    'Party settlements: collect a period’s trips and payments into one basket and close it at parity',
    'Party ledgers with outstanding and ageing, exportable, shareable as an expiring password-protected link, or sent by email or WhatsApp',
    'A collections view that ranks which customers are worth chasing today and sends the payment reminder itself',
    'Job cards for repairs and servicing, costed against the vehicle',
    'Track every tyre individually — fitted, removed, retreaded, scrapped — and parts stock at FIFO cost',
    'Vehicle finance end to end — purchase, loan EMIs matched to bank debits, typed running costs, one-step renewals, and sale',
    'Fuel card balances, limits and transactions for BPCL and Reliance, plus pump credit-bill PDF imports',
    'Bank statement reconciliation that learns from your decisions and suggests fuel recharges, loan EMIs and vehicle costs',
    'Toll transactions recorded against the trips they belong to, with a toll history per vehicle',
    'Tally export, GST filing, e-invoice and e-way bill portal tools, and a TDS register checked against Form 26AS',
    'Trip, expense, vehicle, outstanding, salary, loan and tax reports with export presets and live preview',
    'Live GPS map from your tracking provider or the driver’s phone, trips that advance with the truck, and a fleet wall for the office TV',
    'Renewal and service-due reminders for insurance, PUC, permits, e-way bills and preventive service; driver history on every truck',
    'Multi-user access with role-based permissions, Connected Devices, and Android apps for the office and for your drivers',
];
/** What each client does: `[capability, desktop, web, mobile]`. */
export const platformMatrix = [
    [
        'Trips, parties, vehicles, employees',
        'Create and manage in full',
        'Create and manage in full',
        'Create and edit on a server-hosted workspace; review otherwise',
    ],
    [
        'Job cards',
        'Full workshop workflow',
        'Full workshop workflow',
        'Raise, edit, and photograph on site',
    ],
    [
        'Finance, settlement, and reporting',
        'Full workflows',
        'Full workflows',
        'Receive payments; outstanding and pending reports; share bilty, invoice and statement PDFs',
    ],
    [
        'GPS and live operations',
        'Operations workspace and map',
        'Operations workspace and map',
        'Live fleet map',
    ],
    [
        'Fuel controls',
        'Transactions, limits, and analysis',
        'Transactions, limits, and analysis',
        'Limits and live card controls',
    ],
    ['Tally, GST, and administration', 'Full', 'Full', '—'],
    [
        'Works without a connection',
        'Yes — local database, syncs later',
        'No — needs a connection',
        'Live views only',
    ],
    [
        'Attached documents',
        'Open from your own storage',
        'Open in the browser, from your storage or your office computers',
        'Browse documents and photograph job cards',
    ],
    [
        'Reminders and alerts',
        'In-app reminders and desktop notifications',
        'In-app reminders',
        'Morning alerts pushed to the phone',
    ],
    [
        'Exports and PDFs',
        'Saved and opened on the machine',
        'Printed or downloaded by the browser',
        'Share bilty, invoice, statement and driver account PDFs',
    ],
];
/**
 * The web app, described once for the website's page, the Store description,
 * the FAQ and the JSON-LD.
 */
export const webApp = {
    name: 'Transport Book Web',
    url: urls.webApp,
    tagline: 'The same Transport Book workspace, in any modern browser, with nothing to install.',
    description: 'Sign in at my.transportbook.app and work on the live records your organisation keeps on the Transport Book server — the same screens, the same roles and permissions, and the same numbers the desktop shows. Nothing is installed, nothing needs updating, and a change made in the browser is on every desktop the next time it syncs.',
    /** Who can use it. Access is per organisation, like the rest of the product. */
    availability: 'Available to organisations whose workspace is hosted on the Transport Book server, which every free trial started on the website is. For an older workspace, write to support to confirm whether yours is, and to move it if not.',
    highlights: [
        'The desktop’s screens for trips, job cards, finance, settlements, reports and administration',
        'Attached documents open in the browser, from your storage or your office computers',
        'Sign in on any computer, including one that is not yours, without installing anything',
        'Always the current version: a page reload is the update',
        'The same role-based permissions and the same records as the desktop',
        'Shortcuts, theme and dashboard layout follow your account between desktop and browser',
        'Exports download; PDFs print through the browser, where “Save as PDF” is a printer',
    ],
    /** Modern evergreen browsers. Stated as prose so the list never goes stale. */
    requirements: 'A current version of Chrome, Edge, Firefox or Safari, and an internet connection while you work.',
};
/** How the desktop app and the web app differ: `[aspect, desktop, web]`. */
export const clientComparison = [
    [
        'Install',
        'Microsoft Store or installer on Windows; DMG on macOS',
        'None — open my.transportbook.app',
    ],
    ['Updates', 'Through the Store or the in-app updater', 'Automatic on reload'],
    [
        'Where your records live',
        'A full copy on the machine, synced with the organisation’s workspace',
        'On the Transport Book server; the browser keeps nothing between sessions',
    ],
    [
        'Without a connection',
        'Keeps working; syncs when the line returns',
        'Waits for the connection',
    ],
    [
        'Attached documents',
        'Opens PODs and paperwork from Drive, OneDrive, S3 or your own office computers',
        'Opens the same documents in the browser; connecting storage and checking document health stay on the desktop',
    ],
    [
        'Exports and PDFs',
        'Saved to disk and opened directly',
        'PDFs print via the browser; other files download',
    ],
    ['Screens, roles, and permissions', 'Full', 'Full — the same screens and the same rules'],
    ['Local backups and data folder', 'Yes', 'Not needed — the server holds the data'],
    ['Bulk and external data import', 'Yes', 'No — import on the desktop'],
    ['FASTag portal window', 'Yes', 'No'],
    [
        'Best for',
        'The office machine that must keep working through outages',
        'Any other computer, and a quick sign-in from anywhere',
    ],
];
//# sourceMappingURL=features.js.map